/**
 * The request-list row marker (#1069 Story 2.2; FR53, UX-DR6, UX-DR8, UX-DR9,
 * UX-DR10; D5, superseded in part on 2026-09-11).
 *
 * The third and outermost level of the one unread signal: the thread card, the
 * request header's toggle, and this — a row on the list. With email gone from
 * the feature (#1529) it is THE ONLY WAY anyone notices a reply without opening
 * the request.
 *
 * ⚠️ A STATUS THAT CAN ALSO BE A DOOR. Story 2.2's D5 made this a status and
 * not a control (the row opens the request; the toggle is one tap away). The
 * owner reversed that on 2026-09-11 after seeing it on the list: pressing the
 * marker opens the request WITH the discussion showing, because the marker
 * is the thing that told you there is something to read. So: with `onPress`
 * the marker is a BUTTON, labelled "Open discussion" / "Open discussion,
 * unread", and the screen stops the press reaching the row (the row's own
 * press would open the request with the panel closed, and both would fire on
 * web). Without `onPress` it is what D5 said, an image the row's label
 * includes. `ViewRequests` always passes the handler; the image form remains
 * for any surface that has nowhere to go.
 *
 * ⚠️ NOTHING UNLESS `hasDiscussion === true`. Absent flags mean the server
 * degraded the page (NFR54), the row is an offline `o-` draft that never
 * reached the server, or a cached row from before the flags existed; all draw
 * nothing. `false` draws nothing. Only `true` draws the glyph — the same
 * `MessageSquare` the header toggle draws (UX-DR6), at the row's own size and
 * colour (16px, `colors.textSecondary`, matching Copy beside it) rather than
 * the toggle's.
 *
 * ⚠️ THE DOT OVERHANGS THE GLYPH'S TOP-RIGHT CORNER (owner change 2026-09-23).
 * This paragraph previously said the opposite -- that the dot is a sibling IN
 * FLOW, trailing the glyph, and must never be positioned over the corner,
 * because Android clips children that extend past a rounded parent.
 *
 * ⚠️ AND THE FIRST REWRITE OF THIS PARAGRAPH WAS ALSO WRONG, in a way worth
 * keeping on the record (found at code review the same day). It claimed
 * `glyphBox` -- un-rounded, no `overflow` -- is "what stops Android clipping the
 * overhang", and that this is how `DiscussionToggle` solves it. Both halves are
 * false. Read the render: `glyphBox` is the ONLY child of the touchable, and the
 * touchable is what carries the caller's radius (`actionButton`,
 * `borderRadius: 6`). The dot is therefore a DESCENDANT of the rounded box, and
 * an un-rounded box in between changes nothing about where a clip happens.
 * `DiscussionToggle` is genuinely a different structure: its dot is a sibling of
 * the BUTTON, outside the rounded box entirely. That option is not open here,
 * because the caller owns the outer style and the component cannot hoist a
 * sibling out of it.
 *
 * ⚠️ SO WHAT ACTUALLY KEEPS THE DOT UN-CLIPPED IS THE PADDING, AND NOTHING
 * ELSE. The dot is `UNREAD_DOT_SIZE` (12) pulled `DOT_OVERHANG_TOP` (3) up and
 * `DOT_OVERHANG_RIGHT` (5) across from the glyph, and the two hosts pad by 8
 * (`actionButton`) and 6 (`cardActionIcon`), so it stays inside the touchable's
 * padding box and never reaches its rounded edge. The binding case is 5 against
 * 6: ONE POINT of room, on the card. `view-requests-actions-column-floor.test.ts`
 * guards exactly that inequality, against paddings IMPORTED from the screen.
 *
 * ⚠️ THE GUARD MODELS PADDING, NOT RADIUS, and a corner clips on an ARC. A
 * radius large enough to reach inward past the padding would cut the dot with
 * every assertion still green; at the shipped `borderRadius` of 4 and 6 the dot
 * is clear, but marginally so on the card. Ledgered; only the Android walk can
 * settle it.
 *
 * `glyphBox`'s real and only job is to be the POSITIONING CONTEXT, so `top` and
 * `right` resolve against the 16 px glyph instead of the 32 px button. Without
 * it the same offsets would land the dot somewhere else entirely.
 *
 * The dot is out of flow, so it adds nothing to the marker's laid-out width --
 * which is why the request list's actions-column floor dropped from 88 to 72 in
 * the same change, and why the marker's tappable width dropped with it (48 -> 32
 * on the table, 44 -> 28 on the card; both still clear SC 2.5.8's 24). Ledgered.
 *
 * ⚠️ NO `elevation`, DELIBERATELY -- and `zIndex` is not doing much either.
 * `DiscussionToggle` carries `elevation: 6` because on Android elevation, not
 * JSX order, decides which of two siblings paints on top, and its dot has to
 * beat an ELEVATED `ToolbarActionButton`. Nothing here is elevated and the dot
 * is the later sibling, so document order already paints it above the glyph on
 * all three platforms; the `zIndex: 1` below is belt-and-braces, not the
 * mechanism. Said plainly because Story 8.6 shipped a comment crediting
 * `zIndex` with winning an overlap it could not win.
 *
 * ⚠️ PRESENCE IS THE SIGNAL, NEVER COLOUR (UX-DR8, WCAG 1.4.1). `UnreadDot`
 * renders nothing when there is nothing unread; there is no "read" dot and no
 * hue change. The accessible label carries the state as words, on the
 * wrapper; the dot itself stays hidden from assistive technology, as it is
 * everywhere. No count, anywhere: PRD r8 withdrew quantities.
 *
 * ⚠️ THAT LABEL DOES NOT REACH VOICEOVER ON THE NATIVE CARD, and this component
 * cannot fix it alone. `ViewRequests` renders the card as a `TouchableOpacity`,
 * which defaults to `accessible`, so iOS treats the whole card as one element
 * and hides every descendant label, this one and Copy's alike. Deferred from the
 * 2026-09-11 code review to be fixed for both row actions together (the card
 * needs `accessible={false}`, or the state folded into its own label). On the
 * web table the label is reached normally.
 */

import React from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { UnreadDot } from './UnreadDot';
import { colors } from '../../theme/colors';
import { MessageSquare } from '../icons';

import type { GestureResponderEvent, StyleProp, ViewStyle } from 'react-native';

interface Props {
  /**
   * `true` draws the glyph. `false` and `undefined` both draw nothing — the
   * distinction (computed-empty versus not-computed) is the server's to log
   * and never the row's to show.
   */
  hasDiscussion?: boolean;
  /** `true` adds the dot. Ignored unless `hasDiscussion` is `true`. */
  hasUnread?: boolean;
  /**
   * Makes the marker a button that opens the discussion. The screen owns the
   * navigation AND must stop the event reaching the row (`event.stopPropagation`
   * on web), exactly as `handleCopyRequest` does for Copy.
   */
  onPress?: (event: GestureResponderEvent) => void;
  /** The action's chrome (padding, radius), so it lines up with Copy. */
  style?: StyleProp<ViewStyle>;
  /** The dot is `${testID}-unread`. */
  testID?: string;
}

/** Matches the Copy action beside it on both renderers. */
const GLYPH_SIZE = 16;

/**
 * How far the dot is pulled past the glyph's top and right edges.
 *
 * Replaced the exported `GLYPH_TO_DOT_GAP = 4` on 2026-09-23, when the dot left
 * the flow: there is no longer a gap to size, and the number that matters is the
 * OVERHANG. Exported for the one guard that can now fail --
 * `view-requests-actions-column-floor.test.ts` asserts these stay within the
 * smaller of the two host paddings (`cardActionIcon`'s 6), because an overhang
 * at or past the padding reaches the rounded edge Android clips at.
 *
 * ⚠️ THE TWO AXES ARE NOT THE SAME NUMBER, and that is the owner's eye, not an
 * oversight: both started at 2 and were set to 3 / 5 from the browser on
 * 2026-09-23. Do not "tidy" them back into one constant.
 *
 * ⚠️ `RIGHT` HAS ONE POINT OF ROOM LEFT. The card renderer pads by 6
 * (`cardActionIcon`), so a 5 px overhang clears the rounded edge by 1 px. The
 * web table pads by `spacing.sm` (8) and is not close. Anyone raising this
 * number, or lowering `cardActionIcon`'s padding, is one point from clipping the
 * dot on Android -- which is why the guard compares against the TIGHTER host and
 * not against whichever one is being edited at the time.
 */
export const DOT_OVERHANG_TOP = 3;
export const DOT_OVERHANG_RIGHT = 5;

export const DiscussionRowIndicator: React.FC<Props> = ({
  hasDiscussion,
  hasUnread,
  onPress,
  style,
  testID = 'discussion-row-indicator',
}) => {
  if (hasDiscussion !== true) return null;
  const unread = hasUnread === true;
  const content = (
    <View style={styles.glyphBox}>
      <MessageSquare size={GLYPH_SIZE} color={colors.textSecondary} />
      <UnreadDot
        unread={unread}
        style={styles.dot}
        testID={`${testID}-unread`}
      />
    </View>
  );
  if (onPress) {
    return (
      <TouchableOpacity
        style={[styles.wrap, style]}
        testID={testID}
        onPress={onPress}
        accessible
        accessibilityRole="button"
        accessibilityLabel={
          unread ? 'Open discussion, unread' : 'Open discussion'
        }
        accessibilityHint="Opens the request with its discussion showing"
      >
        {content}
      </TouchableOpacity>
    );
  }
  return (
    <View
      style={[styles.wrap, style]}
      testID={testID}
      accessible
      accessibilityRole="image"
      accessibilityLabel={unread ? 'Has discussion, unread' : 'Has discussion'}
    >
      {content}
    </View>
  );
};

const styles = StyleSheet.create({
  /**
   * The marker itself. It takes the caller's chrome (padding AND radius), which
   * is why it is NOT the box the dot is positioned against -- see the header.
   */
  wrap: {
    alignItems: 'center',
    justifyContent: 'center',
    /**
     * ⚠️ A FLOOR FOR THE FORM THAT HAS NO CALLER CHROME (review, 2026-09-23).
     * `style` is optional and the image form exists "for any surface that has
     * nowhere to go", so the safety argument in the header -- which is about
     * `actionButton`'s 8 and `cardActionIcon`'s 6 -- says nothing about a
     * caller that passes neither. Without this, the dot is pulled past a
     * zero-padding box and sits OUTSIDE the marker's own bounds, where an
     * ancestor's `overflow` or radius can cut it. Both real callers override
     * this with more (8 and 6), so it changes nothing on the list.
     */
    padding: DOT_OVERHANG_RIGHT,
  },
  /**
   * The POSITIONING CONTEXT, and that is all it is: the dot's offsets resolve
   * against the 16 px glyph rather than the 32 px button. It does NOT protect
   * the overhang from Android clipping -- it sits inside the rounded touchable,
   * so a clip would take it too. See the header; that claim was made here once
   * and was false. The padding is the protection.
   */
  glyphBox: {
    position: 'relative',
  },
  /**
   * Top-right of the glyph, where users already expect an unread badge. It
   * overhangs by 3 of its 12 points upward and 5 across -- NOT "half", which is
   * the language this comment was first copied from `DiscussionToggle` with; a
   * 12 px dot on a 16 px glyph occludes roughly its top-right 7x9 px. Out of
   * flow, so the read and unread markers are the same width. `zIndex` is
   * belt-and-braces: document order already paints it above (see the header).
   */
  dot: {
    position: 'absolute',
    top: -DOT_OVERHANG_TOP,
    right: -DOT_OVERHANG_RIGHT,
    zIndex: 1,
  },
});
